import { ProgramEnum } from '../../models/programs';
import { GenericProgramSurvey } from './surveysGeneric';

export enum SurveyQuestionType {
    Text = 'text',
    TextArea = 'textArea',
    Select = 'select',
    DateTime = 'dateTime',
    Rate = 'rate',
    Boolean = 'boolean',
    Options = 'options',
    SingleOption = 'singleOption',
    Steps = 'steps',
    Dummy = 'dummy',
}

export interface SurveyQuestionAnswerBase {
    question: number;
    type: SurveyQuestionType;
    renderId?: string;
    notRequired?: boolean;
}

export interface SurveyQuestionBase {
    id: number;
    type: SurveyQuestionType;
    question?: string;
    placeholder?: string;
    renderId?: string;
    notRequired?: boolean;
}

export interface TextAnswerData {
    content?: string;
}

export interface SurveyTextQuestionAnswer extends SurveyQuestionAnswerBase {
    data: TextAnswerData;
}

export interface OptionsAnswerData {
    options?: string[];
    content?: string;
}

export type SurveyTextQuestion = SurveyQuestionBase;

export interface SurveyOptionsQuestionAnswer extends SurveyQuestionAnswerBase {
    data: OptionsAnswerData;
}

export interface SurveyOptionsQuestion extends SurveyQuestionBase {
    options: string[];
}

export interface SingleOptionAnswerData {
    option?: string;
    content?: string;
}

export interface SurveySingleOptionQuestionAnswer extends SurveyQuestionAnswerBase {
    data: SingleOptionAnswerData;
}

export type SurveySingleOptionQuestion = SurveyOptionsQuestion;

export interface BooleanAnswerData {
    content?: boolean;
}

export interface SurveyBooleanQuestionAnswer extends SurveyQuestionAnswerBase {
    data: BooleanAnswerData;
}

export type SurveyBooleanQuestion = SurveyQuestionBase;

export interface RateAnswerData {
    content?: number;
}

export interface SurveyRateQuestionAnswer extends SurveyQuestionAnswerBase {
    data: RateAnswerData;
}

export interface SurveyRateQuestion extends SurveyQuestionBase {
    range: number;
    minLabel: string;
    maxLabel: string;
    minMaxDescriptions?: string[];
}

export interface StepAnswer {
    id: string;
    content?: string;
}

export interface StepsAnswerData {
    content?: StepAnswer[];
}

export interface SurveyStepsQuestionAnswer extends SurveyQuestionAnswerBase {
    data: StepsAnswerData;
}

export interface SurveyStepsQuestionStep {
    id: string;
    label: string;
    placeholder: string;
}

export interface SurveyStepsQuestion extends SurveyQuestionBase {
    steps: SurveyStepsQuestionStep[];
}

export type SurveySelectQuestionAnswer = SurveyTextQuestionAnswer;

export type SurveySelectQuestion = SurveyOptionsQuestion;

export interface SurveyDateTimeQuestion extends SurveyQuestionBase {
    inputType: string;
}

export interface SurveyDummyQuestion extends SurveyQuestionBase {
    index: number;
    content: React.ReactNode;
}

export type GenericSurveyAnswer =
    | SurveyTextQuestionAnswer
    | SurveyOptionsQuestionAnswer
    | SurveySingleOptionQuestionAnswer
    | SurveyBooleanQuestionAnswer
    | SurveyRateQuestionAnswer
    | SurveyStepsQuestionAnswer
    | SurveySelectQuestionAnswer;

export type GenericSurveyQuestion =
    | SurveyTextQuestion
    | SurveyOptionsQuestion
    | SurveySingleOptionQuestion
    | SurveyBooleanQuestion
    | SurveyRateQuestion
    | SurveyStepsQuestion
    | SurveySelectQuestion
    | SurveyDummyQuestion;

export interface ProgramSurvey {
    questions: GenericSurveyQuestion[];
    answers: GenericSurveyAnswer[];
}

export interface QuestionComponentBaseProps extends SurveyQuestionBase {
    programId: ProgramEnum;
    surveyId: GenericProgramSurvey;
    readableIndex?: number;
}

export type ProgramSurveys = Record<string, ProgramSurvey>;

export type ProgramStatePropsQuestion<T = GenericSurveyQuestion> = T | undefined;

export interface SurveysState {
    programsSurveys: Record<string, ProgramSurveys>;
}

export interface FetchSurveyData<TSurvey = GenericProgramSurvey> {
    programId: ProgramEnum;
    surveyId: TSurvey;
}

export interface FetchSeveralProgramSurveysData<TSurvey = GenericProgramSurvey> {
    programId: ProgramEnum;
    surveysIds: TSurvey[];
}

type FetchedSeveralProgramSurveysData = Record<string, ProgramSurvey>;

export interface NormalizedSeveralSurveysData {
    programId: ProgramEnum;
    programSurveys: FetchedSeveralProgramSurveysData;
}

export interface FetchedSurveyQuestion {
    id: number;
    data: GenericSurveyQuestion;
}

export interface NormalizedSurveyData<TSurvey = GenericProgramSurvey>
    extends FetchSurveyData<TSurvey> {
    questions: GenericSurveyQuestion[];
    answers: GenericSurveyAnswer[];
}

export interface ChangeQuestionAnswerDataBase<TSurvey = GenericProgramSurvey> {
    programId: ProgramEnum;
    surveyId: TSurvey;
    question: number;
}

export interface ChangeSingleValueQuestionAnswerData<
    TSurvey = GenericProgramSurvey,
    TValue = string | number | boolean
> extends ChangeQuestionAnswerDataBase<TSurvey> {
    value: TValue;
}

export type OptionsAnswerField = 'option' | 'content';

export interface ChangeOptionsQuestionAnswerData<TSurvey = GenericProgramSurvey>
    extends ChangeQuestionAnswerDataBase<TSurvey> {
    field: OptionsAnswerField;
    value: string;
}

export type ChangeSingleOptionQuestionAnswerData<
    TSurvey = GenericProgramSurvey
> = ChangeOptionsQuestionAnswerData<TSurvey>;

export interface ChangeStepsQuestionAnswerData<TSurvey = GenericProgramSurvey>
    extends ChangeQuestionAnswerDataBase<TSurvey> {
    stepId: string;
    value: string;
}

export interface SubmitProgramSurveyData {
    programId: ProgramEnum;
    surveyId: GenericProgramSurvey;
    answers: GenericSurveyAnswer[] | undefined;
}

export interface SubmitSeveralProgramSurveysData {
    programId: ProgramEnum;
    answers: AnswersBySurveysIds;
    answeredSurveysAmount?: number;
}

export type AnswersBySurveysIds = Record<string, GenericSurveyAnswer[]>;

export interface SubmitProgramSurveySingleAnswer {
    question: number;
    data?: GenericSurveyAnswer;
}
